import React, { useEffect, useMemo, useState, useCallback , useRef } from "react";
import { Container, Row, Col, Button, Form, Card, Badge, Table } from "react-bootstrap";
import { Calendar, Building2, Home, Clock, Plus, Send } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../asset/css/AdminDailyReport.css";

import { getAllProjectModules } from "../../api/Module";
import { getAllWorkNatures } from "../../api/workNature";
import { getAllClients } from "../../api/client";
import { getBackdateConfig } from "../../api/backdateConfig";

import {
  saveOrUpdateDailyReport,
  getDailyReportByUserAndDate,
  getDailySummaryByUserAndDate,
} from "../../api/DailyReport";

const DailyReporting = () => {
  const token = localStorage.getItem("token");
  const employeeName = localStorage.getItem("userName");
  const userInfoId = localStorage.getItem("id");

  const [modules, setModules] = useState([]);
  const [workNatures, setWorkNatures] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [serverRows, setServerRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [selectedWorkNatureId, setSelectedWorkNatureId] = useState("");
  const [editRow, setEditRow] = useState(null);
const [errors, setErrors] = useState({});
const timeInputRef = useRef(null);
const [dailySummary, setDailySummary] = useState("");
  const [previousSummaryNote, setPreviousSummaryNote] = useState("");
  const [originalDailySummary, setOriginalDailySummary] = useState("");
  

  const getLocalDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = useMemo(() => getLocalDate(), []);

const [form, setForm] = useState({
  workingDate: today,
  workingFrom: "",
  workDetails: "",
  time: "",
  clientName: "",
});

  const MAX_MINUTES = 8 * 60;

  // Allowed backdated days come from Settings > Backdate Configuration (admin)
  const [maxBackdateDays, setMaxBackdateDays] = useState(null);
  useEffect(() => {
    getBackdateConfig(token)
      .then((res) => setMaxBackdateDays(res.data.maxBackdateDays))
      .catch(() => {});
  }, [token]);

  const minAllowedDate = useMemo(() => {
    if (maxBackdateDays == null) return null;
    const d = new Date();
    d.setDate(d.getDate() - maxBackdateDays);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, [maxBackdateDays]);

const normalizeTime = (value) => {
    if (!value) return null;

    const v = value.trim();

    // Accepts:
    //  - "HH:MM" / "H:MM"  e.g. "09:15", "9:15"  -> hour is zero-padded
    //  - "HH" / "H" (hour only, minutes assumed "00") e.g. "9" -> "09:00"
    // Minutes, when present, must still be exactly 2 digits, 00-59.
    const match = /^([0-9]{1,2})(?::([0-5][0-9]))?$/.exec(v);
    if (!match) return null;

    const hours = Number(match[1]);
    const minutesStr = match[2] !== undefined ? match[2] : "00";
    const minutes = Number(minutesStr);
    const paddedHour = String(hours).padStart(2, "0");

    return {
      minutes: hours * 60 + minutes,
      normalized: `${paddedHour}:${minutesStr}`,
    };
  };

  // ✅ HH:MM input mask — digit slot positions inside "HH:MM" (skips the colon at index 2).
  const TIME_MASK = "HH:MM";
  const TIME_SLOT_POSITIONS = [0, 1, 3, 4];
  const buildMaskedTime = (digits) => {
    let di = 0;
    return TIME_MASK.split("").map((ch) =>
      ch === ":" ? ":" : (di < digits.length ? digits[di++] : ch)
    ).join("");
  };

 const minutesToHHMM = (total) => {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  const loadDailyReports = useCallback(
    async (date) => {
      try {
        const res = await getDailyReportByUserAndDate(token, userInfoId, date);

        if (res.data && res.data.length) {
          const mappedRows = res.data.map((r) => ({
            id: r.id,
            workingDate: r.workingDate,
            workingFrom: r.workingFrom,
            workDetails: r.workDetails,
            minutes: r.minutes,
            time: minutesToHHMM(r.minutes),
            projectModuleIds: [r.projectModule?.id],
            workNatureIds: [r.workNature?.id],
            clientId: r.institute?.id,
            clientName: r.institute?.instituteName,
            moduleName: r.projectModule?.projectModuleName || "-",
            workNatureName: r.workNature?.workNatureName || "-",
          }));
        setServerRows(mappedRows);
          setRows(mappedRows);
        } else {
          setServerRows([]);
          setRows([]);
        }

        // The Daily Summary is stored separately from the work-entry rows,
        // so it must be fetched from its own endpoint.
       try {
          const summaryRes = await getDailySummaryByUserAndDate(
            token,
            userInfoId,
            date
          );
          console.log("SUMMARY DEBUG:", date, summaryRes.data);
          const existingSummary = summaryRes.data;
          if (existingSummary && existingSummary.trim()) {
            setDailySummary(existingSummary);
            setOriginalDailySummary(existingSummary);
            setPreviousSummaryNote(
              "This is your previously saved summary for this date. Edit it or add new details below before submitting."
            );
          } else {
            setDailySummary("");
            setOriginalDailySummary("");
            setPreviousSummaryNote("");
          }
        } catch (summaryErr) {
          console.error("Error loading daily summary:", summaryErr);
          setDailySummary("");
          setOriginalDailySummary("");
          setPreviousSummaryNote("");
        }
      } catch (err) {
        console.error("Error loading reports:", err);
        toast.error("Failed to load daily reports");
      }
    },
    [token, userInfoId]
  );

  useEffect(() => {
    getAllProjectModules(token).then((res) => {
      const mappedModules = res.data
        .map((m) => ({
          id: m.moduleId,
          name: m.projectModuleName,
          status: m.status || "Active",
        }))
        // same A-Z order as Admin Reports View
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
    setModules(mappedModules);
});

    getAllWorkNatures(token).then((res) =>
      setWorkNatures(
        res.data.map((w) => ({
          id: w.id,
          name: w.workNatureName,
        }))
      )
    );

    getAllClients(token)
      .then((res) =>
        setClients(
          (res.data || [])
            .map((i) => ({ id: i.id, clientName: i.instituteName }))
            .sort((a, b) => (a.clientName || "").localeCompare(b.clientName || ""))
        )
      )
      .catch(() => toast.error("Failed to load clients"));

    loadDailyReports(today);
  }, [token, today, loadDailyReports]);

  useEffect(() => {
    setRows([...serverRows]);
  }, [serverRows]);

  const resetForm = () => {
    setForm((prev) => ({
      ...prev,
      workingFrom: "",
      workDetails: "",
      time: "",
      clientName: "",
    }));

    setSelectedModuleId("");
    setSelectedWorkNatureId("");
    setSelectedClientId("");
    setEditRow(null);
    setErrors({});
    setRows([...serverRows]);
  };

  const resetFormToToday = () => {
    const todayDate = getLocalDate();

    setForm({
      workingDate: todayDate,
      workingFrom: "",
      workDetails: "",
      time: "",
      clientName: "",
    });

    setSelectedModuleId("");
    setSelectedWorkNatureId("");
    setSelectedClientId("");
    setEditRow(null);
    setErrors({});
    setDailySummary("");
    loadDailyReports(todayDate);
  };
  const getTotalMinutes = (excludeRow = null) => {
    return rows.reduce((sum, r) => {
      if (
        excludeRow &&
        ((r.id && excludeRow.id && r.id === excludeRow.id) ||
          (r.tempId && excludeRow.tempId && r.tempId === excludeRow.tempId))
      ) {
        return sum;
      }
      return sum + (r.minutes || 0);
    }, 0);
  };

  const getRemainingMinutes = () => {
    return Math.max(0, MAX_MINUTES - getTotalMinutes());
  };

  const isValidTaskDetails = (value) => {
    if (!value) return false;
    const v = value.trim();
    const hasLetter = /[a-zA-Z]/.test(v);
    const onlyNumbers = /^[0-9]+$/.test(v);
    const onlySpecial = /^[^a-zA-Z0-9]+$/.test(v);
    return hasLetter && !onlyNumbers && !onlySpecial;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.workingDate) newErrors.workingDate = "Work Date is required";
    if (!form.workingFrom) newErrors.workingFrom = "Please select Work From (Office or WFH)";
    if (!selectedClientId) newErrors.clientName = "Client Name is required";
    if (!selectedModuleId) newErrors.module = "Module is required";
    if (!selectedWorkNatureId) newErrors.workNature = "Work Nature is required";
    if (!form.workDetails) {
      newErrors.workDetails = "Task details are required";
    } else if (!isValidTaskDetails(form.workDetails)) {
      newErrors.workDetails =
        "Task details must contain meaningful text (not only numbers or special characters)";
    }
    if (!form.time.trim()) newErrors.time = "Time is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasAnyChanges = () => {
    const hasNew = rows.some((r) => !r.id);
    const hasDeleted = serverRows.some(
      (sr) => !rows.find((r) => r.id === sr.id)
    );
    const hasUpdated = rows.some((r) => {
      if (!r.id) return false;

      const server = serverRows.find((s) => s.id === r.id);
      if (!server) return false;

      return (
        server.workingFrom !== r.workingFrom ||
        server.workDetails !== r.workDetails ||
        server.minutes !== r.minutes ||
        Number(server.projectModuleIds?.[0]) !==
          Number(r.projectModuleIds?.[0]) ||
        Number(server.workNatureIds?.[0]) !== Number(r.workNatureIds?.[0]) ||
        Number(server.clientId) !== Number(r.clientId)
      );
    });
    const hasSummaryChanged = dailySummary.trim() !== originalDailySummary.trim();

    return hasNew || hasDeleted || hasUpdated || hasSummaryChanged;
  };

  const isSameRow = (a, b) => {
    if (!a || !b) return false;

    if (a.id && b.id) return a.id === b.id;
    if (a.tempId && b.tempId) return a.tempId === b.tempId;

    return false;
  };

  const handleSubmitForm = () => {
    if (!validateForm()) {
      return;
    }

    const parsed = normalizeTime(form.time);
    if (!parsed || parsed.minutes <= 0) {
      toast.error("Enter valid time in HH:MM format with 2-digit hours (e.g. 02:30, 09:15)");
      return;
    }
    const selectedClientObject = clients.find(
      (c) => Number(c.id) === Number(selectedClientId)
    );
    const resolvedClientName = selectedClientObject?.clientName || "";

    const enteredMinutes = parsed.minutes;
    setForm((prev) => ({
      ...prev,
      time: parsed.normalized,
      clientName: resolvedClientName,
    }));

    const existingTotal = getTotalMinutes(editRow);
    const newTotal = existingTotal + enteredMinutes;
    if (newTotal > MAX_MINUTES) {
      toast.error(
        `Working hours exceeded! You already have ${minutesToHHMM(existingTotal)}. ` +
          `Only ${minutesToHHMM(MAX_MINUTES - existingTotal)} left.`
      );
      return;
    }

    const isDuplicate = rows.some((row) => {
      if (editRow && isSameRow(row, editRow)) {
        return false;
      }

      return (
        row.workingDate === form.workingDate &&
        Number(row.projectModuleIds?.[0]) === Number(selectedModuleId) &&
        Number(row.workNatureIds?.[0]) === Number(selectedWorkNatureId)
      );
    });

    if (isDuplicate) {
      toast.error(
        "A task with the same Date, Project Module and Work Nature already exists."
      );
      return;
    }

    if (editRow) {
      setRows((prev) =>
        prev.map((r) => {
          if (isSameRow(r, editRow)) {
            return {
              ...r,
              ...form,
              time: parsed.normalized,
              minutes: enteredMinutes,
              clientId: Number(selectedClientId),
              clientName: resolvedClientName,
              projectModuleIds: [Number(selectedModuleId)],
              workNatureIds: [Number(selectedWorkNatureId)],
              moduleName:
                modules.find((m) => m.id === Number(selectedModuleId))?.name || "-",
              workNatureName:
                workNatures.find((w) => w.id === Number(selectedWorkNatureId))?.name || "-",
            };
          }
          return r;
        })
      );

      setForm((prev) => ({
        ...prev,
        workDetails: "",
        time: "",
        clientName: "",
      }));
      setSelectedModuleId("");
      setSelectedWorkNatureId("");
      setSelectedClientId("");
      setEditRow(null);
      setErrors({});
      toast.success("Entry updated. Click Submit Report to save all changes.");
      return;
    }

    const newRow = {
      ...form,
      minutes: enteredMinutes,
      tempId: Date.now(),
      clientId: Number(selectedClientId),
      clientName: resolvedClientName,
      projectModuleIds: [Number(selectedModuleId)],
      workNatureIds: [Number(selectedWorkNatureId)],
      moduleName: modules.find((m) => m.id === Number(selectedModuleId))?.name || "-",
      workNatureName:
        workNatures.find((w) => w.id === Number(selectedWorkNatureId))?.name || "-",
    };

    setRows((prev) => [...prev, newRow]);
    setForm((prev) => ({
      ...prev,
      workDetails: "",
      time: "",
      clientName: "",
    }));
    setSelectedModuleId("");
    setSelectedWorkNatureId("");
    setSelectedClientId("");
    setErrors({});
    toast.success("Entry added. Click Submit Report when you're done.");
  };
  const handleFinalSubmit = async () => {
    if (rows.length === 0) {
      toast.error("Please add at least one work entry before submitting.");
      return;
    }
    if (!hasAnyChanges()) {
      toast.error(
        "No changes found. Please add or update at least one task before submitting."
      );
      return;
    }
   if (!dailySummary) {
      setErrors((prev) => ({
        ...prev,
        dailySummary: "Daily Summary is required",
      }));
      return;
    } else if (!isValidTaskDetails(dailySummary)) {
      setErrors((prev) => ({
        ...prev,
        dailySummary:
          "Daily Summary must contain meaningful text (not only numbers or special characters)",
      }));
      return;
    }
    try {
      const payload = {
        workingDate: form.workingDate,
        dailySummary: dailySummary,
        reports: rows.map((r) => ({
          projectModuleId: Array.isArray(r.projectModuleIds)
            ? Number(r.projectModuleIds[0])
            : Number(r.projectModuleIds),

          workNatureId: Array.isArray(r.workNatureIds)
            ? Number(r.workNatureIds[0])
            : Number(r.workNatureIds),

          clientId: Number(r.clientId),
          workDetails: r.workDetails,
          minutes: r.minutes,
          workingFrom: r.workingFrom,
        })),
      };
      await saveOrUpdateDailyReport(payload, token, userInfoId);
      toast.success("Daily report submitted successfully");
      setRows([]);
      setServerRows([]);
      resetFormToToday();
    } catch (err) {
      console.error("Submit Error:", err?.response?.data || err);
      const msg = err?.response?.data?.message || err?.response?.data || "Failed to submit daily report";
      toast.error(typeof msg === "string" ? msg : "Failed to submit daily report");
    }
  };

  const handleDelete = (row) => {
    setRows((prev) => prev.filter((r) => !isSameRow(r, row)));

    if (editRow && isSameRow(editRow, row)) {
      setEditRow(null);
      setForm((prev) => ({
        ...prev,
        
        workDetails: "",
        time: "",
        clientName: "",
      }));
      setSelectedModuleId("");
      setSelectedWorkNatureId("");
      setSelectedClientId("");
      setErrors({});
    }

    toast.success("Entry removed. Click Submit Report to save changes.");
  };

  const handleEdit = (row) => {
    setEditRow(row);

    setForm({
      workingDate: row.workingDate,
      workingFrom: row.workingFrom,
      workDetails: row.workDetails,
      time: row.time,
      clientName: row.clientName || "",
    });

    const moduleId = Array.isArray(row.projectModuleIds)
      ? row.projectModuleIds[0]
      : row.projectModuleIds;

    const workNatureId = Array.isArray(row.workNatureIds)
      ? row.workNatureIds[0]
      : row.workNatureIds;

    const matchedClient = clients.find(
      (c) =>
        (row.clientId && Number(c.id) === Number(row.clientId)) ||
        c.clientName === row.clientName
    );
    setSelectedClientId(matchedClient ? Number(matchedClient.id) : "");

    setSelectedModuleId(Number(moduleId));
    setSelectedWorkNatureId(Number(workNatureId));

    setErrors({});
  };

  const handleProjectModule = (e) => {
    const moduleId = e.target.value;
    setErrors((prev) => ({ ...prev, module: "" }));
    setSelectedModuleId(moduleId);
  };

  return (
    <Container fluid className="daily-reporting p-4">
      <div className="dr-header">
        <h4 className="dr-title">Daily Reporting</h4>
        <p className="dr-subtitle">
          Submit daily work updates. {employeeName ? `— ${employeeName}` : ""}
        </p>
      </div>

      {/* Day Information */}
      <Card className="dr-card mb-3">
        <Card.Body>
          <h6 className="dr-section-title">Day Information</h6>
          <Row>
            <Col md={6}>
              <Form.Label>
                Work Date <span className="text-danger">*</span>
              </Form.Label>
              <div className="dr-date-input">
                <Calendar size={16} className="dr-date-icon" />
                <Form.Control
                  type="date"
                  value={form.workingDate}
                  min={minAllowedDate}
                  onClick={(e) => {
                    if (e.target.showPicker) e.target.showPicker();
                  }}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    if (selectedDate && selectedDate < minAllowedDate) {
                      toast.error(
                        `Backdated reporting is allowed only for the last ${maxBackdateDays} days.`                      );
                      return;
                    }
                    // Only update the date — keep whatever the user has
                    // already filled in (client, module, work nature, hours,
                    // work details) untouched until they click Add Entry.
                    setForm((prev) => ({ ...prev, workingDate: selectedDate }));
                    loadDailyReports(selectedDate);
                  }}
                />
              </div>
              {errors.workingDate && (
                <div className="dr-field-error">{errors.workingDate}</div>
              )}
            </Col>

            <Col md={6}>
              <Form.Label>
                Work From <span className="text-danger">*</span>
              </Form.Label>
              <div className="dr-toggle-group">
              <button
  type="button"
  className={`dr-toggle-btn ${form.workingFrom === "Office" ? "active" : ""}`}
  onClick={() => {
    setForm({ ...form, workingFrom: "Office" });
    setErrors((prev) => ({ ...prev, workingFrom: "" }));
  }}
>
  <Building2 size={16} /> Office
</button>
<button
  type="button"
  className={`dr-toggle-btn ${form.workingFrom === "Home" ? "active" : ""}`}
  onClick={() => {
    setForm({ ...form, workingFrom: "Home" });
    setErrors((prev) => ({ ...prev, workingFrom: "" }));
  }}
>
  <Home size={16} /> WFH
</button>
              </div>
              {errors.workingFrom && (
                <div className="dr-field-error">{errors.workingFrom}</div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Work Entries */}
      <Card className="dr-card mb-3">
        <Card.Body>
          <div className="dr-entries-header">
            <div>
              <h6 className="dr-section-title mb-0">Work Entries</h6>
              <p className="dr-subtitle-sm">Add one or more tasks completed today.</p>
            </div>
            <div className="text-end">
              <Badge className="dr-hours-badge">
                <Clock size={14} className="me-1" />
                Total Hours Today: {minutesToHHMM(getTotalMinutes())}
              </Badge>
              <div style={{ marginTop: 6 }}>
                <Badge
                  className="dr-hours-badge"
                  style={{
                    background:
                      getRemainingMinutes() === 0
                        ? "#f8d7da"
                        : getRemainingMinutes() <= 120
                        ? "#fff3cd"
                        : "#d4edda",
                    color:
                      getRemainingMinutes() === 0
                        ? "#721c24"
                        : getRemainingMinutes() <= 120
                        ? "#856404"
                        : "#155724",
                  }}
                >
                  Remaining: {minutesToHHMM(getRemainingMinutes())}
                </Badge>
              </div>
            </div>
          </div>

          {/* Entry form */}
          <div className="dr-entry-block">
            <div className="dr-entry-top">
              <span className="dr-entry-label">
                {editRow ? "EDITING ENTRY" : `ENTRY #${rows.length + 1}`}
              </span>
            </div>

            <Row className="gy-3">
              <Col md={3}>
                <Form.Label>
                  Client Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                    setErrors((prev) => ({ ...prev, clientName: "" }));
                  }}
                >
                  <option value="">Select client</option>
                  {clients.map((c) => (
                    <option key={`client-${c.id}`} value={String(c.id)}>
                      {c.clientName}
                    </option>
                  ))}
                </Form.Select>
                {errors.clientName && (
                  <div className="dr-field-error">{errors.clientName}</div>
                )}
              </Col>

              <Col md={3}>
                <Form.Label>
                  Module <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedModuleId}
                  onChange={handleProjectModule}
                >
                  <option value="">Select module</option>
                  {modules
                    .filter((m) => m.status === "Active" || m.id === Number(selectedModuleId))
                    .map((m) => (
                    <option key={`module-${m.id}`} value={String(m.id)}>
                      {m.name}
                    </option>
                  ))}
                </Form.Select>
                {errors.module && <div className="dr-field-error">{errors.module}</div>}
              </Col>

              <Col md={3}>
                <Form.Label>
                  Work Nature <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedWorkNatureId}
                  onChange={(e) => {
                    setSelectedWorkNatureId(e.target.value);
                    setErrors((prev) => ({ ...prev, workNature: "" }));
                  }}
                >
                  <option value="">Select nature</option>
                  {workNatures.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </Form.Select>
                {errors.workNature && (
                  <div className="dr-field-error">{errors.workNature}</div>
                )}
              </Col>

              <Col md={3}>
                <Form.Label>
                  Working Hours <span className="text-danger">*</span>
                </Form.Label>
              <Form.Control
                  type="text"
                  placeholder="HH:MM"
                  ref={timeInputRef}
                  value={form.time}
                  onKeyDown={(e) => {
                    // If the user has typed only a single hour digit (e.g. "1")
                    // and presses Tab / Enter / ArrowRight ("Next") to move into
                    // the MM section, auto-pad the hour to 2 digits ("1" -> "01")
                    // and jump the cursor into the minutes slot, instead of
                    // requiring the user to manually type the leading zero.
                    if (
                      e.key !== "Tab" &&
                      e.key !== "Enter" &&
                      e.key !== ":" &&
                      e.key !== "ArrowRight"
                    )
                      return;

                    const currentDigits = form.time.replace(/[^0-9]/g, "");
                    if (form.time.includes(":") || currentDigits.length !== 1) return; // only handle single hour digit, no colon yet

                    e.preventDefault();

                    const paddedTime = `${currentDigits.padStart(2, "0")}:`; // "1" -> "01:"
                    setForm((prev) => ({ ...prev, time: paddedTime }));
                    setErrors((prev) => ({ ...prev, time: "" }));

                    const cursorPos = paddedTime.length; // right after the colon
                    setTimeout(() => {
                      if (timeInputRef.current) {
                        timeInputRef.current.focus();
                        timeInputRef.current.selectionStart = cursorPos;
                        timeInputRef.current.selectionEnd = cursorPos;
                      }
                    }, 0);
                  }}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                    const masked = digits.length > 0 ? buildMaskedTime(digits) : "";
                    setForm({ ...form, time: masked });
                    setErrors((prev) => ({ ...prev, time: "" }));
                    const cursorPos = digits.length > 0
                      ? TIME_SLOT_POSITIONS[digits.length - 1] + 1
                      : 0;
                    setTimeout(() => {
                      if (timeInputRef.current) {
                        timeInputRef.current.selectionStart = cursorPos;
                        timeInputRef.current.selectionEnd = cursorPos;
                      }
                    }, 0);
                  }}
                  onKeyDown={(e) => {
                    // If the user has typed only a single hour digit (e.g. "1")
                    // and presses Tab / Enter / ArrowRight ("Next") to move into
                    // the MM section, auto-pad the hour to 2 digits ("1" -> "01")
                    // and jump the cursor into the minutes slot, instead of
                    // letting the next digit fill in as the 2nd hour digit.
                    if (e.key !== "Tab" && e.key !== "Enter" && e.key !== ":" && e.key !== "ArrowRight") return;

                    const currentDigits = form.time.replace(/[^0-9]/g, "");
                    if (currentDigits.length !== 1) return; // only handle single hour digit case

                    e.preventDefault();

                    const paddedHourDigits = currentDigits.padStart(2, "0"); // "1" -> "01"
                    const masked = buildMaskedTime(paddedHourDigits); // "01:MM"
                    setForm((prev) => ({ ...prev, time: masked }));
                    setErrors((prev) => ({ ...prev, time: "" }));

                    const cursorPos = TIME_SLOT_POSITIONS[2]; // first MM slot position
                    setTimeout(() => {
                      if (timeInputRef.current) {
                        timeInputRef.current.focus();
                        timeInputRef.current.selectionStart = cursorPos;
                        timeInputRef.current.selectionEnd = cursorPos;
                      }
                    }, 0);
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (!v) return;
                    const valid = !!normalizeTime(v);
                    setErrors((prev) => ({
                      ...prev,
                      time: valid
                        ? ""
                        : "Enter time in HH:MM format with 2-digit hours (e.g. 02:30, 09:15)",
                    }));
                  }}
                />
              {errors.time && <div className="dr-field-error">{errors.time}</div>}
              
              </Col>

              <Col md={12}>
                <Form.Label>
                  Work Details <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  className="dr-work-details-textarea"
                  placeholder="Describe what was completed in this entry..."
                  value={form.workDetails}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, workDetails: value });
                    if (!value) {
                      setErrors((prev) => ({
                        ...prev,
                        workDetails: "Task details are required",
                      }));
                    } else if (!isValidTaskDetails(value)) {
                      setErrors((prev) => ({
                        ...prev,
                        workDetails:
                          "Task details must contain meaningful text (not only numbers or special characters)",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, workDetails: "" }));
                    }
                  }}
                />
                {errors.workDetails && (
                  <div className="dr-field-error">{errors.workDetails}</div>
                )}
              </Col>
            </Row>
          </div>

          <button
            type="button"
            className="dr-add-entry-btn"
            onClick={handleSubmitForm}
            disabled={getRemainingMinutes() === 0 && !editRow}
          >
            <Plus size={16} className="me-1" /> {editRow ? "Update Entry" : "Add Entry"}
          </button>

         {/* Added entries list (kept so employees can review/edit rows already added) */}
          {rows.length > 0 && (
            <div className="dr-entries-table-wrap">
<Table bordered hover responsive className="dr-entries-table mb-0">
                  <thead>
                  <tr>
                    <th>Date</th>
                    <th>From</th>
                    <th>Client</th>
                    <th>Module</th>
                    <th>Work Nature</th>
                    <th>Task</th>
                   <th>Time</th>
                    <th style={{ whiteSpace: "nowrap", minWidth: 140 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || r.tempId || i}>
                      <td>{r.workingDate}</td>
                      <td>
                        <span
                          className={`dr-from-badge ${
                            r.workingFrom === "Home" ? "home" : "office"
                          }`}
                        >
                          {r.workingFrom === "Home" ? "WFH" : r.workingFrom}
                        </span>
                      </td>
                      <td>{r.clientName || "-"}</td>
                      <td>{r.moduleName || "-"}</td>
                      <td>{r.workNatureName || "-"}</td>
                      <td
                        style={{
                          maxWidth: 260,
                          whiteSpace: "pre-line",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {r.workDetails}
                      </td>
                      <td>
                        <span className="dr-time-pill">{r.time}</span>
                      </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                       <button
                          type="button"
                          onClick={() => handleEdit(r)}
                          style={{
                            position: "static",
                            display: "inline-block",
                            color: "#4f46e5",
                            border: "1px solid #4f46e5",
                            background: "transparent",
                            borderRadius: 4,
                            padding: "4px 10px",
                            marginRight: 6,
                          }}
                        >
                          Edit
                        </button>
                        {!r.id && (
                          <button
                            type="button"
                            className="dr-delete-btn"
                            onClick={() => handleDelete(r)}
                            style={{
                              color: "#dc3545",
                              border: "1px solid #dc3545",
                              background: "transparent",
                              borderRadius: 4,
                              padding: "4px 10px",
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Daily Work Summary */}
      <Card className="dr-card mb-3">
        <Card.Body>
          <h6 className="dr-section-title">Daily Work Summary</h6>
          <Form.Label>
            Daily Summary <span className="text-danger">*</span>
          </Form.Label>
          {previousSummaryNote && (
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
              ℹ️ {previousSummaryNote}
            </div>
          )}
          <Form.Control
            as="textarea"
            rows={5}
            className="dr-work-details-textarea"
            placeholder="Summarize your entire day, blockers, and next steps..."
            value={dailySummary}
            onChange={(e) => {
              const value = e.target.value;
              setDailySummary(value);
              if (!value) {
                setErrors((prev) => ({
                  ...prev,
                  dailySummary: "Daily Summary is required",
                }));
              } else if (!isValidTaskDetails(value)) {
                setErrors((prev) => ({
                  ...prev,
                  dailySummary:
                    "Daily Summary must contain meaningful text (not only numbers or special characters)",
                }));
              } else {
                setErrors((prev) => ({ ...prev, dailySummary: "" }));
              }
            }}
          />
          {errors.dailySummary && (
            <div className="dr-field-error">{errors.dailySummary}</div>
          )}
        </Card.Body>
      </Card>

      <div className="text-end">
        <Button variant="outline-secondary" className="me-2" onClick={resetForm}>
          Cancel
        </Button>
        <Button
          className="dr-submit-btn"
          onClick={handleFinalSubmit}
          disabled={rows.length === 0}
        >
          <Send size={16} className="me-1" /> Submit Report
        </Button>
      </div>

      <ToastContainer />
    </Container>
  );
};

export default DailyReporting;
